import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  AudioOutlined,
  CloseOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { Button, Spin } from 'antd';
import { useTranslate } from '@/translations';
import type {
  MediaSendProgress,
  MediaSendProgressHandler,
  VoiceNote,
} from '../runtime/contracts';
import { buildVoiceWaveform } from '../matrix/media';
import { ComposerActivityTray } from './ComposerActivityTray';

export interface VoiceRecordingSession {
  stop(): Promise<VoiceNote>;
}

interface VoiceRecordingDependencies {
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  createMediaRecorder(stream: MediaStream): MediaRecorder;
  createAudioContext(): AudioContext;
  now(): number;
  setSampleInterval(callback: () => void): number;
  clearSampleInterval(id: number): void;
}

const browserVoiceRecordingDependencies: VoiceRecordingDependencies = {
  getUserMedia: (constraints) =>
    navigator.mediaDevices.getUserMedia(constraints),
  createMediaRecorder: (stream) => new MediaRecorder(stream),
  createAudioContext: () => new AudioContext(),
  now: () => performance.now(),
  setSampleInterval: (callback) => window.setInterval(callback, 100),
  clearSampleInterval: (id) => window.clearInterval(id),
};

export const startBrowserVoiceRecording = async (
  overrides: Partial<VoiceRecordingDependencies> = {},
): Promise<VoiceRecordingSession> => {
  const dependencies = {
    ...browserVoiceRecordingDependencies,
    ...overrides,
  };
  let stream: MediaStream;
  try {
    stream = await dependencies.getUserMedia({ audio: true });
  } catch {
    throw new Error('commsMicrophoneDenied');
  }

  let recorder: MediaRecorder;
  let audioContext: AudioContext;
  try {
    recorder = dependencies.createMediaRecorder(stream);
    audioContext = dependencies.createAudioContext();
  } catch {
    stream.getTracks().forEach((track) => track.stop());
    throw new Error('commsRecordingFailed');
  }

  const source = audioContext.createMediaStreamSource(stream);
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);
  const sampleBuffer = new Uint8Array(analyser.fftSize);
  const samples: number[] = [];
  const chunks: Blob[] = [];
  const startedAt = dependencies.now();
  const sampleInterval = dependencies.setSampleInterval(() => {
    analyser.getByteTimeDomainData(sampleBuffer);
    const squared = sampleBuffer.reduce((total, value) => {
      const normalized = (value - 128) / 128;
      return total + normalized * normalized;
    }, 0);
    samples.push(Math.sqrt(squared / sampleBuffer.length));
  });
  recorder.addEventListener('dataavailable', (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  });
  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    dependencies.clearSampleInterval(sampleInterval);
    source.disconnect();
    stream.getTracks().forEach((track) => track.stop());
    void audioContext.close();
  };
  try {
    recorder.start();
  } catch {
    cleanup();
    throw new Error('commsRecordingFailed');
  }

  let stopPromise: Promise<VoiceNote> | null = null;
  return {
    stop: () => {
      if (stopPromise) return stopPromise;
      stopPromise = new Promise<VoiceNote>((resolve, reject) => {
        recorder.addEventListener(
          'stop',
          () => {
            cleanup();
            const mimeType =
              recorder.mimeType || chunks[0]?.type || 'audio/webm';
            resolve({
              blob: new Blob(chunks, { type: mimeType }),
              durationMs: Math.max(
                1,
                Math.round(dependencies.now() - startedAt),
              ),
              waveform: buildVoiceWaveform(samples),
            });
          },
          { once: true },
        );
        recorder.addEventListener(
          'error',
          () => {
            cleanup();
            reject(new Error('commsRecordingFailed'));
          },
          { once: true },
        );
        try {
          recorder.stop();
        } catch {
          cleanup();
          reject(new Error('commsRecordingFailed'));
        }
      });
      return stopPromise;
    },
  };
};

interface VoiceNoteRecorderProps {
  disabled?: boolean;
  hideTrigger?: boolean;
  onActivityChange?(active: boolean): void;
  onRecorded(
    voiceNote: VoiceNote,
    onProgress: MediaSendProgressHandler,
  ): Promise<void> | void;
  startRecording?: () => Promise<VoiceRecordingSession>;
}

export interface VoiceNoteRecorderHandle {
  start(): Promise<void>;
}

type VoiceRecorderPhase =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'processing'
  | 'uploading-voice'
  | 'sending'
  | 'failed';

const formatElapsed = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  return `${minutes.toString().padStart(2, '0')}:${(seconds % 60)
    .toString()
    .padStart(2, '0')}`;
};

const recordingWaveform = [
  7, 13, 9, 17, 11, 6, 15, 10, 18, 8, 14, 6, 12, 16, 9, 5,
];

const voicePhaseKey = (phase: 'processing' | 'uploading-voice' | 'sending') => {
  switch (phase) {
    case 'processing':
      return 'commsVoicePhase.processing' as const;
    case 'uploading-voice':
      return 'commsVoicePhase.uploading-voice' as const;
    case 'sending':
      return 'commsVoicePhase.sending' as const;
  }
};

export const VoiceNoteRecorder = forwardRef<
  VoiceNoteRecorderHandle,
  VoiceNoteRecorderProps
>(function VoiceNoteRecorder(
  {
    disabled,
    hideTrigger,
    onActivityChange,
    onRecorded,
    startRecording = startBrowserVoiceRecording,
  },
  ref,
) {
  const { t } = useTranslate();
  const sessionRef = useRef<VoiceRecordingSession | null>(null);
  const timerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const startPendingRef = useRef(false);
  const phaseRef = useRef<VoiceRecorderPhase>('idle');
  const [phase, setPhase] = useState<VoiceRecorderPhase>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [voiceNote, setVoiceNote] = useState<VoiceNote | null>(null);
  const [progress, setProgress] = useState<MediaSendProgress | null>(null);
  const [errorKey, setErrorKey] = useState<
    | 'commsMicrophoneDenied'
    | 'commsRecordingFailed'
    | 'commsVoiceUploadFailed'
    | null
  >(null);

  const stopTimer = () => {
    if (timerRef.current === null) return;
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const updatePhase = (nextPhase: VoiceRecorderPhase) => {
    phaseRef.current = nextPhase;
    if (mountedRef.current) setPhase(nextPhase);
  };

  const start = async () => {
    if (disabled || startPendingRef.current || phaseRef.current !== 'idle')
      return;
    startPendingRef.current = true;
    updatePhase('requesting');
    setErrorKey(null);
    setVoiceNote(null);
    setProgress(null);
    try {
      const recordingSession = await startRecording();
      if (!mountedRef.current) {
        await recordingSession.stop().catch(() => undefined);
        return;
      }
      sessionRef.current = recordingSession;
      setElapsed(0);
      updatePhase('recording');
      timerRef.current = window.setInterval(
        () => setElapsed((current) => current + 1),
        1000,
      );
    } catch (error) {
      if (!mountedRef.current) return;
      updatePhase('failed');
      setErrorKey(
        error instanceof Error && error.message === 'commsMicrophoneDenied'
          ? 'commsMicrophoneDenied'
          : 'commsRecordingFailed',
      );
    } finally {
      startPendingRef.current = false;
    }
  };

  useImperativeHandle(ref, () => ({ start }));

  const submit = async (note: VoiceNote) => {
    setErrorKey(null);
    setProgress({ phase: 'uploading-voice' });
    updatePhase('uploading-voice');
    try {
      await onRecorded(note, (nextProgress) => {
        if (!mountedRef.current) return;
        setProgress(nextProgress);
        updatePhase(
          nextProgress.phase === 'sending' ? 'sending' : 'uploading-voice',
        );
      });
      if (!mountedRef.current) return;
      setVoiceNote(null);
      setProgress(null);
      updatePhase('idle');
    } catch {
      if (!mountedRef.current) return;
      setVoiceNote(note);
      updatePhase('failed');
      setErrorKey('commsVoiceUploadFailed');
    }
  };

  const stopAndSend = async () => {
    const session = sessionRef.current;
    if (!session) return;
    sessionRef.current = null;
    stopTimer();
    updatePhase('processing');
    setProgress({ phase: 'processing' });
    try {
      const note = await session.stop();
      setVoiceNote(note);
      await submit(note);
    } catch {
      updatePhase('failed');
      setProgress(null);
      setErrorKey('commsRecordingFailed');
    }
  };

  const cancelRecording = async () => {
    const session = sessionRef.current;
    if (!session) return;
    sessionRef.current = null;
    stopTimer();
    updatePhase('processing');
    try {
      await session.stop();
    } finally {
      setElapsed(0);
      setProgress(null);
      updatePhase('idle');
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      phaseRef.current = 'idle';
      stopTimer();
      const session = sessionRef.current;
      sessionRef.current = null;
      if (session) void session.stop().catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    onActivityChange?.(phase !== 'idle');
  }, [onActivityChange, phase]);

  const progressPercent =
    progress?.loaded !== undefined && progress.total
      ? Math.round((progress.loaded / progress.total) * 100)
      : undefined;

  return (
    <>
      {!hideTrigger && (
        <Button
          disabled={disabled || phase !== 'idle'}
          loading={phase === 'requesting'}
          onClick={() => void start()}
          aria-label={t('commsRecordVoiceNote')}
        >
          {t('commsRecordVoiceNote')}
        </Button>
      )}
      {phase !== 'idle' && (
        <ComposerActivityTray kind="voice" phase={phase}>
          <div className="command-centre-chat__voice-indicator">
            {phase === 'requesting' ? <Spin size="small" /> : <AudioOutlined />}
          </div>
          <div className="command-centre-chat__voice-info">
            <div className="command-centre-chat__voice-status-row">
              <span role={phase === 'failed' ? 'alert' : undefined}>
                {phase === 'recording'
                  ? t('commsRecordingVoice')
                  : phase === 'requesting'
                    ? t('commsRequestingMicrophone')
                    : phase === 'failed'
                      ? t(errorKey ?? 'commsVoiceUploadFailed')
                      : t(voicePhaseKey(phase))}
              </span>
              {phase === 'recording' && (
                <strong>{formatElapsed(elapsed)}</strong>
              )}
            </div>
            {phase === 'recording' ? (
              <div
                className="command-centre-chat__recording-waveform"
                data-testid="comms-recording-waveform"
                aria-hidden="true"
              >
                {recordingWaveform.map((height, index) => (
                  <span key={index} style={{ height }} />
                ))}
              </div>
            ) : (
              <progress
                aria-label={t('commsVoiceUploadProgress')}
                max={100}
                value={progressPercent}
              />
            )}
          </div>
          <div className="command-centre-chat__activity-actions">
            {phase === 'recording' && (
              <>
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  className="command-centre-chat__activity-action"
                  onClick={() => void cancelRecording()}
                  aria-label={t('commsCancelRecording')}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  className="command-centre-chat__activity-action command-centre-chat__activity-action--primary"
                  onClick={() => void stopAndSend()}
                  aria-label={t('commsStopAndSendVoice')}
                />
              </>
            )}
            {phase === 'failed' && (
              <>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  className="command-centre-chat__activity-action"
                  onClick={() => {
                    setVoiceNote(null);
                    setProgress(null);
                    setErrorKey(null);
                    updatePhase('idle');
                  }}
                  aria-label={t('commsDiscardVoiceNote')}
                />
                {voiceNote && (
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    className="command-centre-chat__activity-action command-centre-chat__activity-action--primary"
                    onClick={() => void submit(voiceNote)}
                    aria-label={t('commsRetryVoiceNote')}
                  />
                )}
              </>
            )}
          </div>
        </ComposerActivityTray>
      )}
    </>
  );
});
